import { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IconButton } from '@/components/ui/icon-button';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import type { ReceiptShare } from '@/lib/types';

function relativeExpiry(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.round(diff / (1000 * 60 * 60));
  if (hours < 24) return `Expires in ${hours}h`;
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  return `Expires in ${days} day${days !== 1 ? 's' : ''}`;
}

export default function ReceiptShares() {
  const { toast } = useToast();
  const [shares, setShares] = useState<ReceiptShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/receipts/shares');
      const data = res.data.data || res.data;
      setShares(Array.isArray(data) ? data : data.shares || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const getShareUrl = (share: ReceiptShare) => {
    if (share.shareUrl) return share.shareUrl;
    return `${window.location.origin}/shared/${share.token}`;
  };

  const handleCopy = async (share: ReceiptShare) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(share));
      toast({ title: 'Link copied!' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  const handleDelete = async (shareId: string) => {
    try {
      await api.delete(`/receipts/shares/${shareId}`);
      setShares((prev) => prev.filter((s) => s._id !== shareId));
      toast({ title: 'Share link revoked' });
    } catch {
      toast({ title: 'Failed to revoke', variant: 'destructive' });
    }
    setRevokeTarget(null);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center"
        data-testid="shares-empty"
      >
        <Link2 className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">No shared links</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Share a receipt from its detail view
        </p>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-3" data-testid="receipt-shares">
      {/* Desktop table */}
      <div className="hidden md:block overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2 text-left font-medium">Link</th>
              <th className="px-4 py-2 text-left font-medium">Expires</th>
              <th className="px-4 py-2 text-center font-medium">Accesses</th>
              <th className="hidden px-4 py-2 text-left font-medium lg:table-cell">
                Privacy
              </th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shares.map((share) => {
              const isExpired = new Date(share.expiresAt) < new Date();
              return (
                <tr key={share._id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <p className="max-w-[200px] truncate font-mono text-xs">
                      {getShareUrl(share)}
                    </p>
                  </td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={isExpired ? 'destructive' : 'outline'}
                      className="text-xs"
                    >
                      {relativeExpiry(share.expiresAt)}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {share.accessCount}
                    {share.maxAccesses ? ` / ${share.maxAccesses}` : ''}
                  </td>
                  <td className="hidden px-4 py-2 lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {share.privacySettings.includeAmount && (
                        <Badge variant="secondary" className="text-[10px]">
                          Amount
                        </Badge>
                      )}
                      {share.privacySettings.includeItems && (
                        <Badge variant="secondary" className="text-[10px]">
                          Items
                        </Badge>
                      )}
                      {share.privacySettings.includeMerchant && (
                        <Badge variant="secondary" className="text-[10px]">
                          Merchant
                        </Badge>
                      )}
                      {share.privacySettings.includeNotes && (
                        <Badge variant="secondary" className="text-[10px]">
                          Notes
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* BUG-FE6/FE7: use IconButton with tooltip + min-h-[44px] */}
                      <IconButton
                        tooltip="Copy link"
                        variant="ghost"
                        className="min-h-[44px] min-w-[44px]"
                        onClick={() => handleCopy(share)}
                        data-testid={`share-copy-btn-${share._id}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        tooltip="Open link"
                        variant="ghost"
                        className="min-h-[44px] min-w-[44px]"
                        onClick={() => window.open(getShareUrl(share), '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </IconButton>
                      {/* BUG-FE5: confirm before revoke */}
                      <IconButton
                        tooltip="Revoke"
                        variant="ghost"
                        className="min-h-[44px] min-w-[44px] text-destructive"
                        onClick={() => setRevokeTarget(share._id)}
                        data-testid={`share-revoke-btn-${share._id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {shares.map((share) => {
          const isExpired = new Date(share.expiresAt) < new Date();
          return (
            <div
              key={share._id}
              className="space-y-2 rounded-lg border p-3"
              data-testid={`share-card-${share._id}`}
            >
              <div className="flex items-center justify-between">
                <Badge variant={isExpired ? 'destructive' : 'outline'} className="text-xs">
                  {relativeExpiry(share.expiresAt)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {share.accessCount} access{share.accessCount !== 1 ? 'es' : ''}
                </span>
              </div>
              <p className="break-words font-mono text-xs">{getShareUrl(share)}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] flex-1"
                  onClick={() => handleCopy(share)}
                >
                  <Copy className="mr-1 h-3 w-3" /> Copy
                </Button>
                {/* BUG-FE5: confirm before revoke on mobile */}
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] text-destructive"
                  onClick={() => setRevokeTarget(share._id)}
                  data-testid={`share-revoke-btn-${share._id}`}
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Revoke
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* BUG-FE5: Revoke confirmation */}
    <AlertDialog open={revokeTarget !== null} onOpenChange={(o) => !o && setRevokeTarget(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke share link?</AlertDialogTitle>
          <AlertDialogDescription>
            Anyone with this link will lose access immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => revokeTarget && handleDelete(revokeTarget)}
            data-testid="share-revoke-confirm"
          >
            Revoke
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
